/**
 * Which Firebase project a sign-in belongs to, and the silent handover between
 * them. The server decides through GET /api/auth/config; with its defaults
 * (handover off, sign-ups on studio) — or an older API without the endpoint —
 * everything here reduces to one config call and today's behaviour.
 */

import { useEffect } from 'react';
import type { OAuthCredential, User } from 'firebase/auth';
import {
  type AuthProject,
  activateAuthProject,
  getActiveAuthProject,
  getCurrentUserToken,
  googleRedirectResult,
  otherAuthProject,
  refreshUserToken,
  signInWithGoogle,
  signInWithGoogleCredential,
  signInWithGoogleOn,
  signOutOfAuthProject,
  signOutUser,
  startGoogleRedirect,
  switchAuthProject,
  warmAuthProject,
} from '../../services/firebase';
import { getApiBaseUrl } from '../../utils/apiConfig';
import { useImpersonationStore } from '../../stores/impersonationStore';

export interface AuthConfig {
  signupProject: AuthProject;
  handover: boolean;
  /** The landing capture is open: the notice shows and its beacons go. */
  capture: boolean;
}

const DEFAULT_AUTH_CONFIG: AuthConfig = { signupProject: 'studio', handover: false, capture: false };

const asProject = (value: unknown): AuthProject | null =>
  value === 'studio' || value === 'fourklabs' ? value : null;

export async function getAuthConfig(): Promise<AuthConfig> {
  try {
    const res = await fetch(`${getApiBaseUrl()}/auth/config`);
    if (!res.ok) return DEFAULT_AUTH_CONFIG;
    const body = await res.json();
    return { signupProject: asProject(body?.signupProject) ?? 'studio', handover: body?.handover === true, capture: body?.capture === true };
  } catch {
    return DEFAULT_AUTH_CONFIG;
  }
}

let prefetched: AuthConfig | null = null;

/**
 * The front door reads the config with the page and starts the sign-up project, so the Google click opens its
 * popup at once: Safari blocks a popup that opens after a network wait.
 */
export async function prefetchAuthConfig(): Promise<AuthConfig> {
  const config = await getAuthConfig();
  prefetched = config;
  if (migrationLive(config)) warmAuthProject(config.signupProject);
  return config;
}

/** True once the server has started moving anyone to the fourklabs project. */
const migrationLive = (config: AuthConfig) => config.handover || config.signupProject !== 'studio';

/** The project an email signs in on. Asks /auth/resolve only while the migration is live. */
export async function projectForEmail(email: string): Promise<AuthProject> {
  const config = await getAuthConfig();
  if (!migrationLive(config)) return getActiveAuthProject();
  try {
    const res = await fetch(`${getApiBaseUrl()}/auth/resolve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email.trim() }),
    });
    if (res.ok) return asProject((await res.json())?.project) ?? getActiveAuthProject();
  } catch {
    /* fall through to this browser's project */
  }
  return getActiveAuthProject();
}

export async function projectForSignUp(): Promise<AuthProject> {
  const config = await getAuthConfig();
  return migrationLive(config) ? config.signupProject : getActiveAuthProject();
}

/** The project to retry on when the API refuses this project's token for this account, else null. */
async function projectToRetry(user: User, project: AuthProject): Promise<AuthProject | null> {
  const res = await fetch(`${getApiBaseUrl()}/approval/status`, {
    headers: { Authorization: `Bearer ${await user.getIdToken()}` },
  }).catch(() => null);
  if (!res || (res.status !== 409 && res.status !== 401)) return null;
  const body = await res.json().catch(() => null);
  return asProject(body?.project) ?? otherAuthProject(project);
}

/**
 * Google on the signup project. If the account lives in the other project
 * (409 ACCOUNT_IN_OTHER_PROJECT), sign in there with the same Google credential,
 * so the user never sees the conflict.
 */
export async function signInWithGoogleOnAnyProject(): Promise<User> {
  const config = prefetched ?? (await getAuthConfig());
  if (!migrationLive(config)) return signInWithGoogle();

  const first = config.signupProject;
  const { user, credential } = await signInWithGoogleOn(first);
  return settleGoogleSignIn(first, user, credential);
}

async function settleGoogleSignIn(first: AuthProject, user: User, credential: OAuthCredential | null): Promise<User> {
  const retry = await projectToRetry(user, first);
  if (!retry) {
    activateAuthProject(first);
    return user;
  }
  await signOutOfAuthProject(first);
  const moved = credential
    ? await signInWithGoogleCredential(retry, credential).catch(async () => (await signInWithGoogleOn(retry)).user)
    : (await signInWithGoogleOn(retry)).user;
  activateAuthProject(retry);
  return moved;
}

const REDIRECT_KEY = 'ivy.frontDoor.googleRedirect';

/**
 * Google in this tab, for when the popup is blocked. Only on the fourklabs project: its sign-in runs on this
 * host, so the redirect needs no third-party storage.
 */
export const canContinueInThisTab = (): boolean =>
  prefetched !== null && migrationLive(prefetched) && prefetched.signupProject === 'fourklabs';

export async function continueWithGoogleInThisTab(): Promise<void> {
  sessionStorage.setItem(REDIRECT_KEY, 'fourklabs');
  await startGoogleRedirect('fourklabs');
}

export const googleRedirectPending = (): boolean => {
  try {
    return sessionStorage.getItem(REDIRECT_KEY) === 'fourklabs';
  } catch {
    return false;
  }
};

/** Back from Google in this tab: finish the sign-in like the popup does. Null when this tab sent no one. */
export async function finishGoogleRedirect(): Promise<User | null> {
  if (!googleRedirectPending()) return null;
  sessionStorage.removeItem(REDIRECT_KEY);
  const result = await googleRedirectResult('fourklabs');
  return result ? settleGoogleSignIn('fourklabs', result.user, result.credential) : null;
}

let handedOverFor: string | null = null;

class NotMirroredError extends Error {}

async function requestHandover(token: string): Promise<{ project: AuthProject; customToken: string } | null> {
  const res = await fetch(`${getApiBaseUrl()}/auth/handover`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 204 || res.status === 403) return null;
  if (res.status === 409) throw new NotMirroredError('handover: account not in the target project yet');
  if (!res.ok) throw new Error(`handover HTTP ${res.status}`);
  const body = await res.json();
  const project = asProject(body?.project);
  if (!project || typeof body?.customToken !== 'string') throw new Error('handover: malformed response');
  return { project, customToken: body.customToken };
}

/**
 * After an authenticated load: if the account belongs on the other project,
 * move this browser there without a reload. Once per user per page load,
 * never while an admin is viewing as someone else; retries once (not when the
 * account is not mirrored yet), then stays put and logs.
 */
export async function handOverAuthProject(uid: string): Promise<void> {
  if (useImpersonationStore.getState().isImpersonating) return;
  if (handedOverFor === uid) return;
  handedOverFor = uid;

  const config = await getAuthConfig();
  if (!config.handover) return;

  const token = await getCurrentUserToken();
  if (!token) return;
  const meRes = await fetch(`${getApiBaseUrl()}/me`, { headers: { Authorization: `Bearer ${token}` } });
  if (meRes.status === 401) {
    const body = await meRes.json().catch(() => null);
    if (isProjectNotAccepted(body)) {
      const verdict = await afterUnauthorized(body, false);
      if (verdict !== 'retry') await leaveForLogin(verdict);
    }
    return;
  }
  if (!meRes.ok) return;
  const me = await meRes.json();
  const target = asProject(me?.user?.authProject);
  if (!target || target === getActiveAuthProject()) return;

  let lastError: unknown;
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      const handover = await requestHandover(token);
      if (handover) await switchAuthProject(handover.project, handover.customToken);
      return;
    } catch (err) {
      lastError = err;
      if (err instanceof NotMirroredError) break;
    }
  }
  console.warn(`[auth] handover to ${target} failed; staying on ${getActiveAuthProject()}`, lastError);
}

const isProjectNotAccepted = (body: unknown) =>
  (body as { code?: unknown } | null)?.code === 'AUTH_PROJECT_NOT_ACCEPTED';

let moving: Promise<boolean> | null = null;
let refreshing: Promise<string | null> | null = null;
let leaving: Promise<void> | null = null;

/** The server rejected this project's token: ask it for a custom token on the account's project. */
async function moveToAccountProject(): Promise<boolean> {
  const token = await getCurrentUserToken();
  if (!token) return false;
  try {
    const res = await fetch(`${getApiBaseUrl()}/auth/handover`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 204) return true;
    if (!res.ok) return false;
    const body = await res.json();
    const project = asProject(body?.project);
    if (!project || typeof body?.customToken !== 'string') return false;
    await switchAuthProject(project, body.customToken);
    return true;
  } catch (err) {
    console.warn('[auth] handover after a rejected token failed', err);
    return false;
  }
}

export type UnauthorizedVerdict = 'retry' | 'signin-moved' | 'signed-out';

/**
 * What to do with a 401. Every concurrent request shares one handover (for
 * AUTH_PROJECT_NOT_ACCEPTED) or one token refresh (anything else), and a
 * request is retried at most once.
 */
export async function afterUnauthorized(body: unknown, alreadyRetried: boolean): Promise<UnauthorizedVerdict> {
  const notAccepted = isProjectNotAccepted(body);
  if (leaving || alreadyRetried) return notAccepted ? 'signin-moved' : 'signed-out';
  if (notAccepted) {
    moving ??= moveToAccountProject().finally(() => { moving = null; });
    return (await moving) ? 'retry' : 'signin-moved';
  }
  refreshing ??= refreshUserToken().catch(() => null).finally(() => { refreshing = null; });
  const fresh = await refreshing;
  if (!fresh) return 'signed-out';
  localStorage.setItem('bearer_token', fresh);
  return 'retry';
}

export function signOutOfBothProjects(): Promise<void> {
  leaving ??= (async () => {
    await signOutUser().catch(() => {});
    await Promise.all((['studio', 'fourklabs'] as const).map((p) => signOutOfAuthProject(p).catch(() => {})));
  })();
  return leaving;
}

export const isLeavingForLogin = () => leaving !== null;

export async function leaveForLogin(verdict: Exclude<UnauthorizedVerdict, 'retry'>): Promise<void> {
  const first = !leaving;
  await signOutOfBothProjects();
  if (!first) return;
  console.error('[AUTH] signed out after an unrecoverable 401, redirecting to /login. Path:', window.location.pathname);
  window.location.href = verdict === 'signin-moved' ? '/login?reason=signin-moved' : '/login';
}

export function useAuthHandover(uid: string | undefined, ready: boolean): void {
  useEffect(() => {
    if (ready && uid) void handOverAuthProject(uid);
  }, [uid, ready]);
}
